/**
 * PortalDocumentPreviewDialog — preview online de um Documento da Central (E21.3.4, FR-8).
 *
 * Busca a info de preview (proxy same-origin assinado) e renderiza:
 * - PDF → <iframe> (visor nativo do browser; passa no CSP frame-src 'self');
 * - imagem → <img>;
 * - Office/outros (previewable=false) → mensagem + "Baixar" (OnlyOffice read-only no
 *   portal fica p/ E21.3.4b).
 * O acesso é auditado server-side (`central.document.previewed`) na chamada do preview.
 */
import { useEffect, useState } from "react";
import { AlertCircle, Download, FileQuestion, Loader2 } from "lucide-react";
import { toast } from "sonner";

import {
  portalCentralApi,
  type DocumentPreviewInfo,
} from "@/services/portalApi";
import { triggerBrowserDownload } from "@/lib/browser-download";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export interface PortalDocumentPreviewDialogProps {
  doc: { id: string; title: string };
  onClose: () => void;
}

export function PortalDocumentPreviewDialog({
  doc,
  onClose,
}: PortalDocumentPreviewDialogProps) {
  const [info, setInfo] = useState<DocumentPreviewInfo | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let alive = true;
    setState("loading");
    portalCentralApi
      .getPreviewInfo(doc.id)
      .then((d) => {
        if (!alive) return;
        setInfo(d);
        setState("ready");
      })
      .catch(() => alive && setState("error"));
    return () => {
      alive = false;
    };
  }, [doc.id]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const dl = await portalCentralApi.getDownloadInfo(doc.id);
      triggerBrowserDownload(dl.download_url);
    } catch {
      toast.error("Não foi possível baixar o documento.");
    } finally {
      setDownloading(false);
    }
  };

  const isPdf = info?.content_type === "application/pdf";
  const isImage = info?.content_type?.startsWith("image/");

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-h-[90vh] overflow-hidden sm:max-w-4xl"
        data-testid="portal-doc-preview-dialog"
      >
        <DialogHeader>
          <DialogTitle className="truncate pr-6">{doc.title}</DialogTitle>
        </DialogHeader>

        {state === "loading" ? (
          <div className="flex items-center justify-center py-20 text-muted-foreground">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Carregando preview…
          </div>
        ) : state === "error" || !info ? (
          <div className="flex flex-col items-center gap-3 py-16 text-sm text-muted-foreground">
            <AlertCircle className="h-6 w-6 text-destructive" />
            <span>Não foi possível carregar o preview.</span>
            <Button variant="outline" size="sm" onClick={handleDownload}>
              <Download className="mr-2 h-4 w-4" />
              Baixar
            </Button>
          </div>
        ) : info.previewable && isPdf ? (
          <iframe
            src={info.url}
            title={doc.title}
            className="h-[75vh] w-full rounded-md border"
            data-testid="portal-doc-preview-pdf"
          />
        ) : info.previewable && isImage ? (
          <div className="flex max-h-[75vh] justify-center overflow-auto">
            <img
              src={info.url}
              alt={doc.title}
              className="max-h-[75vh] object-contain"
              data-testid="portal-doc-preview-image"
            />
          </div>
        ) : (
          <div
            className="flex flex-col items-center gap-3 py-16 text-center text-sm text-muted-foreground"
            data-testid="portal-doc-preview-unsupported"
          >
            <FileQuestion className="h-10 w-10 text-muted-foreground/50" />
            <p>
              Pré-visualização não disponível para este tipo de arquivo.
              <br />
              Baixe para abrir no seu computador.
            </p>
            <Button onClick={handleDownload} disabled={downloading}>
              {downloading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              Baixar
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default PortalDocumentPreviewDialog;
